import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../app/redux/hooks';
import logger from '../../common/logger/AppLogger';
import { PinEntryDevice, useCardPreAuthTransactionMutation, useGetPinEntryDevicesQuery } from '../api';
import { useOperatorSession } from '../appState';
import { showErrorDialog } from '../error-dialog/errorDialogSlice';
import { setTransaction, useTransaction } from '../order/orderSlice';

function PreAuthPinEntryDeviceModal() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useAppDispatch();
  const transactionId = Number(state.activeTransactionId ?? '0');

  const operator = useOperatorSession();
  const { data: pinEntryDevices, isLoading: pinEntryDevicesLoading } = useGetPinEntryDevicesQuery(false);
  const transaction = useTransaction();
  const [requestPreAuth] = useCardPreAuthTransactionMutation();

  const [processing, setProcessing] = useState(false);

  const requestPreAuthPayment = (terminal: PinEntryDevice) => {
    if (processing) return;
    if (transactionId <= 0 || !operator) return;

    setProcessing(true);
    const amount = state.amount > 0 ? state.amount : null;
    requestPreAuth({
      pinEntryDeviceId: terminal.id,
      transactionId: transactionId,
      operatorId: operator.operator.id,
      amount: amount,
    })
      .unwrap()
      .then((tx) => {
        const otherTx = transaction.filter((p) => p.id !== tx.id);
        dispatch(setTransaction([...otherTx, tx]));
        navigate('../');
      })
      .catch((err) => {
        logger.error(err);
        if (err && err.status === 412) {
          dispatch(
            showErrorDialog({
              message: 'Card Payment already in progress for transaction',
              dismissible: true,
              buttonText: 'Close',
            }),
          );
        } else if (err && err.status === 424) {
          dispatch(
            showErrorDialog({
              message: err.data,
              dismissible: true,
              buttonText: 'Close',
            }),
          );
        } else {
          dispatch(
            showErrorDialog({
              message: 'An error occurred requesting pre-authorization',
              dismissible: true,
              buttonText: 'Close',
            }),
          );
        }
        navigate('../');
      });
  };

  const PinEntryDeviceTile = ({ position }: { position: number }) => {
    const index = position - 1;
    if (pinEntryDevices && pinEntryDevices.length >= position) {
      const pinEntryDevice = pinEntryDevices[index];
      return (
        <div className="pos-pin-entry-devices__device" onClick={() => requestPreAuthPayment(pinEntryDevice)}>
          <h3 className="pos-pin-entry-devices__device-text">{pinEntryDevice.name}</h3>
          <p className="pos-pin-entry-devices__device-meta">
            <span className="pos-pin-entry-devices__device-meta-line">{pinEntryDevice.terminalId}</span>
          </p>
        </div>
      );
    }
    return <div className="pos-pin-entry-devices__device hidden-button"></div>;
  };

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay') navigate('../');
        }}
      >
        <div className="pos-modal pos-modal_role_pin-entry-devices">
          <div className="pos-modal__content">
            <div className="pos-pin-entry-devices js-pin-entry-devices">
              <div className="pos-pin-entry-devices__header">
                <h2 className="pos-pin-entry-devices__title">Pre-Authorize on PED</h2>
                <p className="pos-pin-entry-devices__subtitle">Select a PED to take a card pre-authorization:</p>
              </div>
              {pinEntryDevicesLoading == false && pinEntryDevices?.length == 0 && (
                <div className="pos-pin-entry-devices__empty">No Pin Entry Devices are available</div>
              )}
              <div className="pos-pin-entry-devices__info">
                <div className="pos-pin-entry-devices__devices">
                  <div className="pos-pin-entry-devices__devices-list">
                    <div className="pos-pin-entry-devices__devices-scroll js-pin-entry-devices-servers-scroll">
                      <div className="pos-pin-entry-devices__devices-row">
                        <div className="pos-pin-entry-devices__devices-group">
                          <div className="pos-pin-entry-devices__devices-row">
                            <PinEntryDeviceTile position={1} />
                            <PinEntryDeviceTile position={2} />
                            <PinEntryDeviceTile position={3} />
                          </div>
                          <div className="pos-pin-entry-devices__devices-row">
                            <PinEntryDeviceTile position={4} />
                            <PinEntryDeviceTile position={5} />
                            <PinEntryDeviceTile position={6} />
                          </div>
                          <div className="pos-pin-entry-devices__devices-row">
                            <PinEntryDeviceTile position={7} />
                            <PinEntryDeviceTile position={8} />
                            <PinEntryDeviceTile position={9} />
                          </div>
                          <div className="pos-pin-entry-devices__devices-row">
                            <PinEntryDeviceTile position={10} />
                            <PinEntryDeviceTile position={11} />
                            <PinEntryDeviceTile position={12} />
                          </div>
                          <div className="pos-pin-entry-devices__devices-row">
                            <PinEntryDeviceTile position={13} />
                            <PinEntryDeviceTile position={14} />
                            <PinEntryDeviceTile position={15} />
                          </div>
                          <div className="pos-pin-entry-devices__devices-row">
                            <PinEntryDeviceTile position={16} />
                            <PinEntryDeviceTile position={17} />
                            <PinEntryDeviceTile position={18} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreAuthPinEntryDeviceModal;
